import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _isEmpty from 'lodash/isEmpty';
import { Box, Button, Typography } from '@material-ui/core';
import MaterialReactTable from 'material-react-table';
import TableContainer from '@mui/material/TableContainer';
import PxTableColumns from '../report/PxTableColumns';
import FileIcon from '../../assets/FileIcon';
import ZeroState from './ZeroState';

export default function VirtualizedTable({
  data,
  handleEmailReport,
  tabId,
  customFilterComponent,
  handleOpenInsightPopup,
  batchSize,
  setIndividualDealerId,
}) {
  const tableContainerRef = useRef(null);
  const rowVirtualizerInstanceRef = useRef(null);

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [loadedBatch, setLoadedBatch] = useState(1);

  const batchedData = useMemo(() => data?.list?.slice(0, batchSize * loadedBatch), [data, batchSize, loadedBatch]);

  const totalFetched = batchedData?.length;

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (scrollHeight - scrollTop - clientHeight < 400 && totalFetched < data.list?.length) {
          setLoadedBatch((prevBatch) => prevBatch + 1);
        }
      }
    },
    [totalFetched, data.list],
  );

  useEffect(() => {
    try {
      rowVirtualizerInstanceRef.current?.scrollToIndex?.(0);
    } catch (error) {
      console.error(error);
    }
  }, [sorting, columnFilters, globalFilter]);

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    setSorting([]);
  }, [tabId]);

  return (
    <TableContainer style={{ maxWidth: '72vw' }}>
      {!_isEmpty(batchedData) ? (
        <MaterialReactTable
          columns={PxTableColumns(tabId, handleOpenInsightPopup, setIndividualDealerId)}
          data={batchedData}
          enableColumnActions
          enableColumnFilters
          enablePagination={false}
          enableSorting
          enableBottomToolbar={false}
          enableTopToolbar
          enableFullScreenToggle
          muiTableBodyRowProps={{ hover: true }}
          enableStickyHeader
          enableMultiRowSelection={false}
          rowCount={data.list.length}
          enablePinning
          enableMultiSort
          enableColumnResizing
          enableDensityToggle={false}
          initialState={{ density: 'compact' }}
          muiTableProps={{
            sx: {
              tableLayout: 'fixed',
            },
          }}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: '38vh', marginBottom: '0px' },
            onScroll: (event) => fetchMoreOnBottomReached(event.target),
          }}
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
          onSortingChange={setSorting}
          state={{
            columnFilters,
            globalFilter,
            isLoading: false,
            showProgressBars: false,
            sorting,
          }}
          rowVirtualizerInstanceRef={rowVirtualizerInstanceRef}
          rowVirtualizerOptions={{ overscan: 4 }}
          renderTopToolbarCustomActions={() => (
            <Box style={{ display: 'flex', width: '100%', gap: '10px', alignItems: 'center' }}>
              {tabId === 0 && customFilterComponent()}

              <Button style={{ marginRight: '20px' }} onClick={() => handleEmailReport('PX_REPORT')}>
                <Typography color="primary" style={{ borderBottom: '1px solid #622FEE' }} variant="text">
                  Email PX Report
                </Typography>
              </Button>
              <Button onClick={() => handleEmailReport('USAGE_REPORT')}>
                <Typography color="primary" style={{ borderBottom: '1px solid #622FEE' }} variant="text">
                  Email Usage Report
                </Typography>
              </Button>
              <Button onClick={() => handleEmailReport('TOM_REPORT')}>
                <Typography color="primary" style={{ borderBottom: '1px solid #622FEE' }} variant="text">
                  Email TOM Report
                </Typography>
              </Button>
            </Box>
          )}
        />
      ) : (
        <ZeroState
          Icon={FileIcon}
          text="No data found for the applied filters. Please change the filters and try again."
          style={{ paddingBottom: '100px', marginTop: '100px' }}
        />
      )}
    </TableContainer>
  );
}
